/**
 * Load suite app rail from People `GET /api/v1.0/config/apps/` (falls back to static config).
 */

import { type AppConfig, getEnabledApps } from "./apps.config";

export const SUITE_CONFIG_APPS_URL = "/api/v1.0/config/apps/";
export const SUITE_CONFIG_APPS_RUNTIME_URL = "/api/v1.0/config/apps/runtime/";

/** API item shape from People `SuiteAppsConfigView`. */
export interface SuiteAppApiItem {
  id: string;
  label: string;
  path: string;
  icon: string;
  url: string;
  enabled: boolean;
  description: string;
  requires_super_admin?: boolean;
  sidebar_order?: number;
  use_beta_version?: boolean;
  current_version?: string;
  beta_version?: string;
  custom_config?: Record<string, unknown>;
  navigation?: {
    custom_label?: string;
    is_visible?: boolean;
    display_order?: number;
  };
}

export interface SuiteAppsRuntimePayload {
  apps: SuiteAppApiItem[];
  by_app_id: Record<string, SuiteAppApiItem>;
}

function mapApiToAppConfig(row: SuiteAppApiItem): AppConfig {
  return {
    id: row.id,
    label: row.label,
    path: row.path.startsWith("/") ? row.path : `/${row.path}`,
    icon: row.icon,
    url: row.url.startsWith("/") ? row.url : `/${row.url}`,
    enabled: row.enabled !== false,
    description: row.description || "",
    requiresSuperAdmin: Boolean(row.requires_super_admin),
    sidebarOrder: row.sidebar_order ?? 0,
    useBetaVersion: Boolean(row.use_beta_version),
    currentVersion: row.current_version ?? "",
    betaVersion: row.beta_version ?? "",
    customConfig: row.custom_config ?? {},
    navigation: row.navigation
      ? {
          customLabel: row.navigation.custom_label ?? "",
          isVisible: row.navigation.is_visible ?? true,
          displayOrder: row.navigation.display_order ?? row.sidebar_order ?? 0,
        }
      : undefined,
  };
}

/**
 * Fetch merged app list for the current user. Falls back to static `apps.config` on error.
 */
export async function fetchSuiteAppsFromApi(
  getAccessToken: () => Promise<string | null>
): Promise<AppConfig[]> {
  const token = await getAccessToken();
  if (!token) {
    return getEnabledApps();
  }
  try {
    const res = await fetch(SUITE_CONFIG_APPS_URL, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      return getEnabledApps();
    }
    const data = (await res.json()) as { apps?: SuiteAppApiItem[] };
    const apps = data.apps?.map(mapApiToAppConfig) ?? [];
    return apps.length > 0 ? apps : getEnabledApps();
  } catch {
    return getEnabledApps();
  }
}

export async function fetchSuiteAppsRuntimeConfig(
  getAccessToken: () => Promise<string | null>
): Promise<SuiteAppsRuntimePayload | null> {
  const token = await getAccessToken();
  if (!token) return null;
  try {
    const res = await fetch(SUITE_CONFIG_APPS_RUNTIME_URL, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as Partial<SuiteAppsRuntimePayload>;
    if (!Array.isArray(data.apps) || !data.by_app_id) return null;
    return {
      apps: data.apps,
      by_app_id: data.by_app_id,
    };
  } catch {
    return null;
  }
}
